/**
 * Render a quote/change-order line item description.
 *
 * Two formats supported:
 *  1. HTML (from the new RichDescriptionEditor) — rendered through DOMPurify
 *  2. Plain text (legacy) — newlines preserved, lines starting with -, *, • become bullets
 */
import DOMPurify from "dompurify";

const ALLOWED_TAGS = ["p", "strong", "em", "b", "i", "u", "ul", "ol", "li", "br"];
const ALLOWED_ATTR = [];

const isHtml = (text) => /<\/?(p|ul|ol|li|strong|em|b|i|br)\b/i.test(text);

export const FormattedDescription = ({ text, className }) => {
  if (!text) return null;

  // HTML branch — sanitise and render
  if (isHtml(text)) {
    const clean = DOMPurify.sanitize(text, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
    });
    return (
      <div
        className={`${className || ""} [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0`}
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    );
  }

  // Plain-text branch (legacy)
  const lines = text.split(/\r?\n/);
  const blocks = [];
  let currentList = null;

  const isBullet = (line) => /^\s*[-*•]\s+/.test(line);
  const stripBullet = (line) => line.replace(/^\s*[-*•]\s+/, "");

  lines.forEach((rawLine, idx) => {
    const line = rawLine.trimEnd();
    if (isBullet(line)) {
      if (!currentList) {
        currentList = { type: "ul", items: [] };
        blocks.push(currentList);
      }
      currentList.items.push(stripBullet(line));
    } else if (line.trim() === "") {
      currentList = null;
      if (blocks.length > 0 && blocks[blocks.length - 1]?.type !== "spacer") {
        blocks.push({ type: "spacer" });
      }
    } else {
      currentList = null;
      blocks.push({ type: "p", text: line, key: idx });
    }
  });

  return (
    <div className={className}>
      {blocks.map((b, i) => {
        if (b.type === "ul") {
          return (
            <ul key={`ul-${i}`} className="list-disc pl-5 space-y-0.5 my-1">
              {b.items.map((it, j) => <li key={j}>{it}</li>)}
            </ul>
          );
        }
        if (b.type === "spacer") {
          return <div key={`sp-${i}`} className="h-2" />;
        }
        return <p key={`p-${i}`} className="my-1 leading-snug">{b.text}</p>;
      })}
    </div>
  );
};

export default FormattedDescription;
